import TTLProxy from "../TTLAsyncProxy";
import {TractorRequestEndpoints} from "../ProxyEndpoints";
import NotificationHandler from "../NotificationHandler";
import {Permissions, UserRoles} from "../Constants";
import NotificationChannels from "../NotificationChannels";
import NotificationEvents from "../NotificationEvents";
import {
    broadcastEvent, notifyOperatorRequestUpdate,
    notifyPaymentsUpdate,
    notifyRequestUpdate,
    notifyUserProfileChanged
} from "../Events/EventSource";
import {getSetting} from "../LocalSettings/LocalSettings";
import Utils from "../Utils";
import EventActions from "../Events/Actions";
import UserNotifications from "./UserNotifications";
import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {hasAllPermissions} from "../../components/PermissionRender/PermissionRender";

export const ActiveNotificationTypes = {
    RequestCompleted: 'request:completed',
    RequestAssigned: 'request:assigned',
    RequestCancelled: 'request:cancelled',
    RequestBegin: "request:begin",
    RequestCreated: 'request:created',
    OperatorRequestCreated: "operator_request:created",
    RequestPaid: "request:payment_success",
    GeofenceEnter: "geofence:enter",
    GeofenceExit: "geofence:exit",
    UserRegistered: "user:register",
    PaymentSent: "payment:sent",
    PaymentReceived: "payment:received",
};

export class ChannelSubscription extends React.Component{

    _subscription;
    _notif = NotificationHandler.getInstance();

    static propTypes = {
        channel: PropTypes.any,
        event: PropTypes.any,
        tag: PropTypes.string,
        onParsed: PropTypes.func,
        callback: PropTypes.func
    };

    componentDidMount() {

        const { channel , event , tag , onParsed , callback } = this.props;

        //  close subscription if active
        if(this._subscription)
            this._subscription.close();

        this._subscription = this._notif.subscribeChannelEvent(channel,event,tag,async (data) => {

            if(_.isFunction(callback)){
                await callback(data);
            }

            //
            if(_.isFunction(onParsed)){

                Utils.parseNotificationData(data, payload => {
                    onParsed(payload);
                });

            }

        });

    }

    componentWillUnmount() {

        if(this._subscription){
            this._subscription.close();
            this._subscription = null;
        }

    }

    render(){
        return <React.Fragment>{this.props.children}</React.Fragment>
    }

}

//  FETCH TRACTOR REQUEST SERVICE
function fetchRequestService(request,args) {

    if(request.service){
        return Promise.resolve(request.service);
    }

    const proxy = TTLProxy.getInstance();
    return proxy.processRequest(TractorRequestEndpoints.getService(request.id).url , args || {  } ).then(response =>{
        const svc = response.getPayload();
        request.service = svc;
        return svc;
    });

}

/**
 * Checks notification is allowed
 * @param {String} notification
 * @param permissions
 * @param departments
 * @return {*|boolean}
 */
const isAllowedNotification = (notification,permissions,departments) => {

    const user = TTLProxy.getInstance().getUserInfo();

    const isNotificationsEnabled = () => {

        switch (user.role) {
            case UserRoles.Admin:
                return user.admin.request_notification;
            case UserRoles.Owner:
                return user.owner.request_notification;
            default:
                return true;
        }

    };

    return getSetting("use_popup") && user && (user.notifications || []).includes(notification) && (isNotificationsEnabled()) &&
        (permissions ? hasAllPermissions(user.permissions || [],permissions) : true) &&
        (departments && user.admin ? (departments.every(d => user.admin.departments.includes(d)))  : true );
};

/**
 * Subscribe to channels common for all users
 */
function registerCommonChannels(notif,user) {

    const userRole = user.role;

    //  Handle new notification created
    notif.subscribeChannelEvent(NotificationChannels.UserNotification , NotificationEvents.generic.create , "user_listener" , (data) => {


        Utils.parseNotificationData(data, notif => {

            const frame = notif.affected.find(x => x.user_role === userRole);
            if(frame && frame.user_id.indexOf(user.id) >= 0){

                //  broadcast notification update
                broadcastEvent(EventActions.NotificationUpdate);

            }


        });

    });

    //  subscribe to media channels
    (function subscribeMediaChannels() {

        /**
         *  Checks whether media payload belongs to the current user
         */
        function isUserMedia(payload) {

            const entity = payload.entity;

            switch (entity.name){
                case "owner":
                    return userRole === UserRoles.Owner && user.owner.id == entity.id;
                case "admin":
                    return userRole === UserRoles.Admin && user.admin.id == entity.id;
                case "farmer":
                    return userRole === UserRoles.Farmer && user.farmer.id == entity.id;
                case "fbo":
                    return userRole === UserRoles.FBO && user.fbo.id == entity.id;
                case "ccagent":
                    return userRole === UserRoles.CallCenterAgent && user.call_center_agent.id == entity.id;
            }
        }

        notif.subscribeChannelEvent(NotificationChannels.Media , NotificationEvents.generic.create , "media_create" , data => {

            Utils.parseNotificationData(data, payload => {

                if(isUserMedia(payload)){
                    notifyUserProfileChanged(payload.media);
                }

            });

        });

        notif.subscribeChannelEvent(NotificationChannels.Media , NotificationEvents.generic.edit , "media_update" , data => {

            Utils.parseNotificationData(data, payload => {

                if(isUserMedia(payload)){
                    notifyUserProfileChanged(payload.media);
                }

            });

        });

        notif.subscribeChannelEvent(NotificationChannels.Media, NotificationEvents.generic.delete , "media_remove" , data => {

            Utils.parseNotificationData(data, payload => {

                if(isUserMedia(payload)){
                    notifyUserProfileChanged(payload.media);
                }

            });

        });

    })();
}

/**
 * Subscribe to channels for administrators
 * @param notif
 * @param user
 */
function registerAdminChannels(notif,user) {

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests , NotificationEvents.tractor_requests.request_assigned, "admin_user_listener" , (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestAssigned,[Permissions.Requests.View])){

            Utils.parseNotificationData(data, payload => {
                fetchRequestService(payload.Request).then(() => UserNotifications.notifyRequestAssigned(payload.Request));
            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests , NotificationEvents.tractor_requests.request_completed, "admin_user_listener" , (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestCompleted,[Permissions.Requests.View])){

            Utils.parseNotificationData(data, payload => {
                fetchRequestService(payload.Request).then(() => UserNotifications.notifyRequestCompleted(payload.Request));
            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests , NotificationEvents.tractor_requests.request_released, "admin_user_listener" , (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestCancelled,[Permissions.Requests.View])){

            Utils.parseNotificationData(data, payload => {
                fetchRequestService(payload.Request).then(() => UserNotifications.notifyRequestCancelled(payload.Request));
            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests,NotificationEvents.generic.create, "admin_user_listener", (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestCreated,[Permissions.Requests.View])) {

            Utils.parseNotificationData(data, request => {
                fetchRequestService(request).then(() => UserNotifications.notifyNewTractorRequest(request));
            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests, NotificationEvents.tractor_requests.request_paid , "admin_user_listener" , (data) =>{

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestPaid,[Permissions.Requests.View])) {

            Utils.parseNotificationData(data, request => {
                fetchRequestService(request).then(() => UserNotifications.notifyTractorRequestPayment(request));
            });
        }

    });

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests, NotificationEvents.tractor_requests.request_begin, "admin_user_listener" , (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestBegin,[Permissions.Requests.View])) {

            Utils.parseNotificationData(data, request => {
                fetchRequestService(request).then(() => UserNotifications.notifyRequestStarted(request));
            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.Payments, NotificationEvents.generic.create, "admin_user_listener" , (data) => {
        Utils.parseNotificationData(data, payment => notifyPaymentsUpdate(payment));
    });

    notif.subscribeChannelEvent(NotificationChannels.OperatorRequests, NotificationEvents.generic.create, "admin_user_listener" , (data) => {

        //  Notify operator request update
        notifyOperatorRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.OperatorRequestCreated,[Permissions.Operators.View])) {
            Utils.parseNotificationData(data, request => UserNotifications.notifyOperatorRegistration(request));
        }

    });

    notif.subscribeChannelEvent(NotificationChannels.Admin , NotificationEvents.admin.user_register , "admin_user_listener", data => {

        if(isAllowedNotification(ActiveNotificationTypes.UserRegistered)){
            Utils.parseNotificationData(data, payload => UserNotifications.notifyNewUserRegistration(payload));
        }

    });

}

/**
 * Subscribe to channels for farmers and FBO
 * @param notif
 * @param user
 */
function registerFBOFarmerChannels(notif,user) {

    const userRole = user.role;

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests , NotificationEvents.tractor_requests.request_assigned, "farmer_user_listener" , (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestAssigned,[Permissions.Requests.View])){

            Utils.parseNotificationData(data, payload => {

                if((userRole === UserRoles.FBO && payload.FBOId === user.fbo.id) || (userRole === UserRoles.Farmer && payload.FarmerId === user.farmer.id)){
                    fetchRequestService(payload.Request).then(() => UserNotifications.notifyRequestAssigned(payload.Request));
                }

            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests , NotificationEvents.tractor_requests.request_begin, "farmer_user_listener" , (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestBegin,[Permissions.Requests.View])){

            Utils.parseNotificationData(data, payload => {

                if((userRole === UserRoles.FBO && payload.FBOId === user.fbo.id) || (userRole === UserRoles.Farmer && payload.FarmerId === user.farmer.id)){
                    fetchRequestService(payload.Request).then(() => UserNotifications.notifyRequestStarted(payload.Request));
                }

            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests , NotificationEvents.tractor_requests.request_completed, "farmer_user_listener" , (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestCompleted,[Permissions.Requests.View])){

            Utils.parseNotificationData(data, payload => {

                if((userRole === UserRoles.FBO && payload.FBOId === user.fbo.id) || (userRole === UserRoles.Farmer && payload.FarmerId === user.farmer.id)){
                    fetchRequestService(payload.Request).then(() => UserNotifications.notifyRequestCompleted(payload.Request));
                }

            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests , NotificationEvents.tractor_requests.request_released, "farmer_user_listener" , (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestCancelled,[Permissions.Requests.View])){

            Utils.parseNotificationData(data, payload => {

                if((userRole === UserRoles.FBO && payload.FBOId === user.fbo.id) || (userRole === UserRoles.Farmer && payload.FarmerId === user.farmer.id)){
                    fetchRequestService(payload.Request).then(() => UserNotifications.notifyRequestCancelled(payload.Request));
                }

            });

        }

    });

}

/**
 * Subscribe to channels for tractor owners
 * @param notif
 * @param user
 */
function registerTractorOwnerChannels(notif,user) {

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests , NotificationEvents.tractor_requests.request_assigned, "owner_user_listener" , (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestAssigned,[Permissions.Requests.View])){

            Utils.parseNotificationData(data, payload => {

                //  My Notification
                if(payload.TractorOwnerId == user.owner.id) {
                    fetchRequestService(payload.Request).then(() => UserNotifications.notifyRequestAssigned(payload.Request));
                }

            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests , NotificationEvents.tractor_requests.request_completed, "owner_user_listener" , (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestCompleted,[Permissions.Requests.View])){

            Utils.parseNotificationData(data, payload => {

                //  My Notification
                if(payload.TractorOwnerId == user.owner.id) {
                    fetchRequestService(payload.Request).then(() => UserNotifications.notifyRequestCompleted(payload.Request));
                }

            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests , NotificationEvents.tractor_requests.request_released, "owner_user_listener" , (data) => {

        //
        notifyRequestUpdate(data);

        if(isAllowedNotification(ActiveNotificationTypes.RequestCancelled,[Permissions.Requests.View])){

            Utils.parseNotificationData(data, payload => {

                //  My Notification
                if(payload.TractorOwnerId == user.owner.id) {
                    fetchRequestService(payload.Request).then(() => UserNotifications.notifyRequestCancelled(payload.Request));
                }

            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.TractorRequests , NotificationEvents.tractor_requests.request_begin, "owner_user_listener" , (data) => {

        if(isAllowedNotification(ActiveNotificationTypes.RequestBegin,[Permissions.Requests.View])){

            Utils.parseNotificationData(data, payload => {

                //  My Notification
                if(payload.TractorOwnerId == user.owner.id) {
                    fetchRequestService(payload.Request).then(() => UserNotifications.notifyRequestCancelled(payload.Request));
                }

            });

        }

    });

    notif.subscribeChannelEvent(NotificationChannels.Payments, NotificationEvents.generic.create, "admin_user_listener" , (data) => {

        if(isAllowedNotification(ActiveNotificationTypes.PaymentReceived,[Permissions.Payments.View])){
            Utils.parseNotificationData(data, payment => {

                if(payment.owner_id == user.owner.id)
                    UserNotifications.notifyPaymentReceived(payment);

            });
        }

    });

}

/**
 * Subscribe to realtime notifications channels
 * @param user The currently logged in user
 */
export const subscribeNotificationChannels = (user) => {

    //  subscribe to global events
    const notif = NotificationHandler.getInstance({

        connected: () => {

            const userRole = user.role;
            switch (userRole){

                case UserRoles.Farmer:
                case UserRoles.FBO:
                    registerFBOFarmerChannels(notif,user);
                    break;

                case UserRoles.Owner:
                    registerTractorOwnerChannels(notif,user);
                    break;

                case UserRoles.Admin:
                    registerAdminChannels(notif,user);
                    break;
            }

            //  common channels
            registerCommonChannels(notif,user);
        }

    });

};




// WEBPACK FOOTER //
// ./src/common/UserNotifications/Subscriptions.js